<template>
    <card title="AR Past-Due Queue">
        <b-container fluid>
            <div v-if="!arEnabled" class="text-center p-3 text-muted">
                Accounts Receivable is not enabled for this pharmacy.
            </div>
            <template v-else>
                <b-row class="mb-2 align-items-end">
                    <b-col cols="3">
                        <label class="mb-1">As Of</label>
                        <b-form-datepicker v-model="asOf" @input="loadItems" />
                    </b-col>
                    <b-col class="text-right">
                        <b-button v-if="canGenerate && selectedItems.length" variant="primary" class="mr-2"
                            :disabled="working" @click="generateStatementsForSelected">
                            Generate Statements ({{ selectedItems.length }})
                        </b-button>
                        <b-button variant="outline-secondary" :disabled="working || !items.length" @click="exportCsv">
                            Export CSV
                        </b-button>
                    </b-col>
                </b-row>

                <b-table :fields="fields" :items="items" :busy="loading" small responsive show-empty
                    selectable select-mode="multi" sort-by="pastDueTotal" :sort-desc="true"
                    @row-selected="onRowSelected">
                    <template #table-busy>
                        <div class="text-center my-2"><b-spinner small /> Loading...</div>
                    </template>
                    <template #empty>No past-due accounts.</template>
                </b-table>
            </template>
        </b-container>
    </card>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { ARPastDueAccount, arAccountStatusLabel } from "@/models/AR";
import axios from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    name: "ARPastDueQueuePage",
})
export default class ARPastDueQueuePage extends Vue {
    private arEnabled = false;
    private loading = false;
    private working = false;
    private items: ARPastDueAccount[] = [];
    private selectedItems: ARPastDueAccount[] = [];
    private asOf: string = moment().format("YYYY-MM-DD");

    get canGenerate(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Statement/Generate Single");
    }

    get fields() {
        const money = (v: number) => this.currency(v);
        return [
            { key: "accountNumber", label: "Account", sortable: true },
            { key: "responsiblePatientId", label: "Responsible Patient", sortable: true },
            { key: "status", label: "Status", formatter: (v: number) => arAccountStatusLabel(v) },
            { key: "balance", label: "Balance", sortable: true, formatter: money },
            { key: "agingCurrent", label: "Current", formatter: money },
            { key: "agingBucket1", label: "31-60", formatter: money },
            { key: "agingBucket2", label: "61-90", formatter: money },
            { key: "agingBucket3", label: "91-120", formatter: money },
            { key: "agingBucket4Plus", label: "120+", formatter: money },
            { key: "pastDueTotal", label: "Past Due", sortable: true, formatter: money },
        ];
    }

    async mounted(): Promise<void> {
        await this.loadConfiguration();
        if (this.arEnabled) await this.loadItems();
    }

    private async loadConfiguration(): Promise<void> {
        try {
            const response = await axios.get<boolean>("/configuration/enableAR");
            this.arEnabled = response.data === true;
        } catch {
            this.arEnabled = false;
        }
    }

    async loadItems(): Promise<void> {
        if (!this.arEnabled) return;
        try {
            this.loading = true;
            const response = await axios.get<ARPastDueAccount[]>("/AR/past-due", { params: { asOf: this.asOf } });
            this.items = response.data ?? [];
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            this.items = [];
        } finally {
            this.loading = false;
        }
    }

    onRowSelected(rows: ARPastDueAccount[]): void {
        this.selectedItems = rows;
    }

    async generateStatementsForSelected(): Promise<void> {
        const periodEnd = moment(this.asOf);
        const periodStart = periodEnd.clone().subtract(1, "month");
        const dueByDate = periodEnd.clone().add(30, "days");
        try {
            this.working = true;
            for (const account of this.selectedItems) {
                await axios.post(`/AR/accounts/${account.arAccountId}/statements`, {
                    periodStart: periodStart.format("YYYY-MM-DD"),
                    periodEnd: periodEnd.format("YYYY-MM-DD"),
                    dueByDate: dueByDate.format("YYYY-MM-DD"),
                });
            }
            this.$notification(NotificationOptions.success(`Generated ${this.selectedItems.length} statement(s).`));
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.working = false;
        }
    }

    async exportCsv(): Promise<void> {
        try {
            this.working = true;
            const response = await axios.get("/AR/past-due/export", {
                params: { asOf: this.asOf },
                responseType: "blob",
            });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `ar-past-due-${this.asOf}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.working = false;
        }
    }

    currency(value?: number | null): string {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
    }
}
</script>

<style scoped></style>
